import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './PokemonProfile.css';
import { formatFormLabel } from '../utils/forms';
import { trackPokemonView } from '../utils/analytics';

const TYPE_COLORS = {
  Bug: '#A8B820', Dark: '#705848', Dragon: '#7038F8', Electric: '#F8D030',
  Fairy: '#EE99AC', Fighting: '#C03028', Fire: '#F08030', Flying: '#A890F0',
  Ghost: '#705898', Grass: '#78C850', Ground: '#E0C068', Ice: '#98D8D8',
  Normal: '#A8A878', Poison: '#A040A0', Psychic: '#F85888', Rock: '#B8A038',
  Steel: '#B8B8D0', Water: '#6890F0'
};

const TIER_COLORS = {
  'S': 'linear-gradient(135deg, #b8860b, #ffd700)',
  'A+': 'linear-gradient(135deg, #7c8a96, #c0c0c0)',
  'A': 'linear-gradient(135deg, #8b5e3c, #cd7f32)',
  'B+': '#3b82f6',
  'B': '#6366f1',
  'C+': '#64748b',
};

const STAT_MAX = 300;

function getBaseName(pokemonName) {
  return pokemonName
    .replace(/\s*\(Mega[^)]*\)/, '')
    .replace(/\s*\(Shadow\)/, '')
    .trim();
}

function getCategoryLabel(category) {
  if (category === 'mega') return 'Mega';
  if (category === 'non_mega') return 'Non-Mega';
  return 'Standard';
}

function getRankingsForName(allTierLists, name) {
  if (!allTierLists) return [];
  const results = [];
  for (const [type, tierData] of Object.entries(allTierLists)) {
    if (type === 'attack_type') continue;
    for (const category of ['mega', 'non_mega']) {
      if (!tierData[category]) continue;
      for (const entry of tierData[category]) {
        const baseName = getBaseName(entry.pokemon_name);
        if (baseName.toLowerCase() === name.toLowerCase()) {
          results.push({ ...entry, attackType: type, category });
        }
      }
    }
  }
  results.sort((a, b) => a.rank - b.rank || b.combined_effective_dps - a.combined_effective_dps);
  return results;
}

function filterRankingsByForm(rankings, selectedForm) {
  if (selectedForm === 'All') return rankings;
  return rankings.filter(entry => {
    const name = entry.pokemon_name;
    const isMega = /\(Mega[^)]*\)/.test(name);
    const isShadow = /\(Shadow\)/.test(name);

    if (selectedForm === 'Shadow') return isShadow;
    if (selectedForm.startsWith('Mega')) return isMega;
    // For Normal or regional forms (Alola, Galarian, etc.), filter by form field and exclude mega/shadow
    if (!isMega && !isShadow) return entry.form === selectedForm;
    return false;
  });
}

function TierBadge({ tier }) {
  if (!tier) return null;
  const bg = TIER_COLORS[tier] || '#64748b';
  const style = bg.startsWith('linear') ? { background: bg } : { backgroundColor: bg };
  return (
    <span className="tier-badge" style={style}>
      {tier}
    </span>
  );
}

function StatBar({ label, value, color }) {
  const percentage = Math.min((value / STAT_MAX) * 100, 100);
  return (
    <div className="stat-bar-row">
      <span className="stat-bar-label">{label}</span>
      <div className="stat-bar-track">
        <div className="stat-bar-fill" style={{ width: `${percentage}%`, backgroundColor: color }} />
      </div>
      <span className="stat-bar-value">{value}</span>
    </div>
  );
}

function getFormLabel(entry) {
  const match = entry.pokemon_name.match(/\(([^)]+)\)/);
  if (match) return match[1];
  if (entry.form && entry.form !== 'Normal') return formatFormLabel(entry.form);
  return 'Normal';
}

function MiniRankingCard({ entry, onClick }) {
  const isMega = entry.category === 'mega';
  return (
    <div className="mini-ranking-card mini-ranking-card-clickable" onClick={onClick}>
      <span
        className="mini-ranking-type"
        style={{ backgroundColor: TYPE_COLORS[entry.attackType] || '#666' }}
      >
        {entry.attackType}{isMega ? ', Mega' : ''}
      </span>
      <TierBadge tier={entry.tier} />
      <span className="mini-ranking-name">{getFormLabel(entry)}</span>
      <span className="mini-ranking-dps-value">{entry.combined_effective_dps} DPS</span>
    </div>
  );
}

function PokemonProfile({ allTierLists, onChallengeRaidBoss }) {
  const { pokemonName } = useParams();
  const decodedName = decodeURIComponent(pokemonName);
  const navigate = useNavigate();
  const [pokemonDataMap, setPokemonDataMap] = useState(null);
  const [selectedForm, setSelectedForm] = useState('All');

  useEffect(() => {
    fetch('/data/pokemon_data.json')
      .then(res => res.json())
      .then(data => setPokemonDataMap(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (decodedName) trackPokemonView(decodedName);
  }, [decodedName]);

  // Reset form selection when navigating to a different Pokemon
  useEffect(() => {
    setSelectedForm('All');
  }, [decodedName]);

  const rankings = useMemo(
    () => getRankingsForName(allTierLists, decodedName),
    [allTierLists, decodedName]
  );

  const pokemonData = pokemonDataMap ? pokemonDataMap[decodedName] : null;
  const icon = pokemonData?.icon_filename || (rankings.length > 0 ? rankings[0].icon_filename : null);

  // Get stats/types for selected form
  const activeStats = useMemo(() => {
    if (!pokemonData) return null;
    if (selectedForm === 'All' || !pokemonData.form_stats || !pokemonData.form_stats[selectedForm]) {
      return {
        base_attack: pokemonData.base_attack,
        base_defense: pokemonData.base_defense,
        base_stamina: pokemonData.base_stamina,
        types: pokemonData.types,
      };
    }
    return pokemonData.form_stats[selectedForm];
  }, [pokemonData, selectedForm]);

  // Filter rankings by selected form
  const filteredRankings = useMemo(
    () => {
      const filtered = filterRankingsByForm(rankings, selectedForm);
      filtered.sort((a, b) => a.rank - b.rank || b.combined_effective_dps - a.combined_effective_dps);
      return filtered;
    },
    [rankings, selectedForm]
  );

  // Build evolution chain rankings, filtered by form
  const evolutionData = useMemo(() => {
    if (!pokemonData?.evolution_chain || !allTierLists) return null;
    if (pokemonData.evolution_chain.length <= 1) return null;
    return pokemonData.evolution_chain.map(member => {
      const memberRankings = getRankingsForName(allTierLists, member.name);
      const filtered = filterRankingsByForm(memberRankings, selectedForm);
      filtered.sort((a, b) => a.rank - b.rank || b.combined_effective_dps - a.combined_effective_dps);
      return {
        ...member,
        rankings: filtered
      };
    });
  }, [pokemonData, allTierLists, selectedForm]);

  const isShadowSelected = selectedForm === 'Shadow';

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const baseTitle = 'Pokemon GO Type Tier Lists';
    const title = pokemonData
      ? `${decodedName} | Pokemon GO Tier Lists & Raid Rankings`
      : baseTitle;

    document.title = title;

    const descriptionFallback =
      pokemonData?.description ||
      `Stats, evolution line and raid rankings for ${decodedName} in Pokémon GO.`;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionFallback);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', descriptionFallback);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', descriptionFallback);
    }
  }, [decodedName, pokemonData]);

  const handleRankingClick = (entry) => {
    const pokemonKey = `${entry.pokemon_name}-${entry.form}`;
    navigate(`/?type=${encodeURIComponent(entry.attackType)}&pokemon=${encodeURIComponent(pokemonKey)}`);
  };

  if (!allTierLists) {
    return (
      <div className="profile-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const notFound = !pokemonData && rankings.length === 0;

  return (
    <div className="profile-page">
      <Link to="/" className="back-link">← Back to Tier Lists</Link>

      {notFound ? (
        <div className="profile-not-found">
          <h2>No data found for "{decodedName}"</h2>
          <p>This Pokémon may not exist in the database.</p>
        </div>
      ) : (
        <>
          {/* Header Card */}
          <div className={`profile-header-card ${isShadowSelected ? 'shadow' : ''}`}>
            {icon && (
              <div className={`profile-icon-wrap ${isShadowSelected ? 'profile-icon-wrap--shadow' : ''}`}>
                {isShadowSelected && (
                  <span className="profile-icon-bubble" aria-hidden="true" />
                )}
                <img
                  src={`/pokemon_icons/${icon}`}
                  alt={decodedName}
                  className="profile-icon"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div className="profile-header-info">
              {onChallengeRaidBoss && (
                <button
                  className="profile-raid-btn"
                  onClick={() => onChallengeRaidBoss(decodedName)}
                >
                  Simulate Raid Boss
                </button>
              )}
              <h1 className="profile-name">{decodedName}</h1>
              {activeStats?.types && (
                <div className="profile-types">
                  {activeStats.types.map(t => (
                    <span
                      key={t}
                      className="profile-type-badge"
                      style={{ backgroundColor: TYPE_COLORS[t] || '#666' }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {pokemonData?.forms && pokemonData.forms.length > 0 && (
                <div className="profile-variants">
                  <button
                    className={`profile-variant-btn ${selectedForm === 'All' ? 'active' : ''}`}
                    onClick={() => setSelectedForm('All')}
                  >
                    All
                  </button>
                  {pokemonData.forms.map(f => (
                    <button
                      key={f}
                      className={`profile-variant-btn ${selectedForm === f ? 'active' : ''}`}
                      onClick={() => setSelectedForm(f)}
                    >
                      {formatFormLabel(f)}
                    </button>
                  ))}
                </div>
              )}
              {rankings.length > 0 && (
                <p className="profile-summary">
                  Appears in {rankings.length} tier list {rankings.length === 1 ? 'entry' : 'entries'} across{' '}
                  {new Set(rankings.map(r => r.attackType)).size} type{new Set(rankings.map(r => r.attackType)).size === 1 ? '' : 's'}
                </p>
              )}
              {rankings.length === 0 && (
                <p className="profile-summary">Not ranked in any type tier list</p>
              )}
            </div>
          </div>

          {/* Stats Section */}
          {activeStats && (
            <div className="stats-section">
              <h2 className="profile-section-title">Base Stats{selectedForm !== 'All' ? ` — ${selectedForm}` : ''}</h2>
              <div className="stats-bars">
                <StatBar label="Attack" value={activeStats.base_attack} color="#ef5350" />
                <StatBar label="Defense" value={activeStats.base_defense} color="#42a5f5" />
                <StatBar label="Stamina" value={activeStats.base_stamina} color="#66bb6a" />
              </div>
            </div>
          )}

          {/* Evolution Chain Section */}
          {evolutionData && (
            <div className="evolution-section">
              <h2 className="profile-section-title">Evolution Chain{selectedForm !== 'All' ? ` — ${selectedForm}` : ''}</h2>
              <div className="evolution-chain">
                {evolutionData.map((member, idx) => (
                  <React.Fragment key={member.pokemon_id}>
                    <div className={`evolution-member ${member.name.toLowerCase() === decodedName.toLowerCase() ? 'evolution-member-current' : ''}`}>
                      <Link to={`/pokemon/${encodeURIComponent(member.name)}`} className="evolution-link">
                        <img
                          src={`/pokemon_icons/${member.icon_filename}`}
                          alt={member.name}
                          className="evolution-icon"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="evolution-name">{member.name}</span>
                      </Link>
                      {member.rankings.length > 0 ? (
                        <div className="evolution-rankings">
                          {member.rankings.map((entry, ridx) => (
                            <MiniRankingCard key={ridx} entry={entry} onClick={() => handleRankingClick(entry)} />
                          ))}
                        </div>
                      ) : (
                        <div className="evolution-no-rankings">Not ranked{selectedForm !== 'All' ? ` (${selectedForm})` : ''}</div>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Type Rankings Section */}
          {rankings.length > 0 && (
            <>
              <h2 className="profile-section-title">Type Rankings{selectedForm !== 'All' ? ` — ${selectedForm}` : ''}</h2>
              {filteredRankings.length > 0 ? (
                <div className="profile-rankings">
                  {filteredRankings.map((entry, idx) => (
                    <div key={idx} className="ranking-card ranking-card-clickable" onClick={() => handleRankingClick(entry)}>
                      <div
                        className="ranking-type-badge"
                        style={{ backgroundColor: TYPE_COLORS[entry.attackType] || '#666' }}
                      >
                        {entry.attackType}
                      </div>
                      <div className="ranking-details">
                        <div className="ranking-top-row">
                          <span className="ranking-pokemon-name">{entry.pokemon_name}</span>
                          <div className="ranking-top-badges">
                            <TierBadge tier={entry.tier} />
                            <span className="ranking-category">{getCategoryLabel(entry.category)}</span>
                          </div>
                        </div>
                        <div className="ranking-rank">
                          #{entry.rank} in {entry.attackType} {getCategoryLabel(entry.category)}
                        </div>
                        <div className="ranking-moves">
                          <div className="ranking-move">
                            <span className="ranking-move-label">Fast:</span> {entry.fast_move}
                            {entry.elite_fast_tm && <span className="ranking-elite">Elite TM</span>}
                          </div>
                          <div className="ranking-move">
                            <span className="ranking-move-label">Charged:</span> {entry.charged_move}
                            {entry.elite_charged_tm && <span className="ranking-elite">Elite TM</span>}
                          </div>
                        </div>
                        <div className="ranking-dps-row">
                          <div className="ranking-dps">
                            <span className="ranking-dps-label">L50 DPS</span>
                            <span className="ranking-dps-value">{entry.combined_effective_dps}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="profile-no-rankings">
                  No rankings found for the {selectedForm} form
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default PokemonProfile;
